<template>
  <div class="indicator-header" :layout="layout">
    <div class="indicator-header__section meta">
      <div class="indicator-header__mode-switcher">
        <a
          v-if="!(isEmbedded || isPrintable)"
          class="indicator-header__reset-chart-btn"
          @click="resetChart"
          >Reset</a
        >
        <a
          v-if="!(isEmbedded || isPrintable)"
          class="indicator-header__mode-switcher-btn"
          @click="switchMode"
          >Compare</a
        >
      </div>
    </div>
    <div class="indicator-header__section main">
      <div class="indicator-header__icon" :style="{ backgroundImage: iconURL }" />
      <div class="indicator-header__title">{{ indName }}</div>
    </div>
    <div class="indicator-header__section flips">
      <flip-selector
        v-if="flipDimensions.length"
        :disabled="breakdownDimensions.length == 0"
        :is-loading="isLoading"
        :dimensions="flipDimensions"
      />
    </div>
    <div class="indicator-header__section breakdowns" :cols="dropdownColumns">
      <div
        v-for="dim in breakdownDimensions"
        :key="dim.id"
        class="indicator-header__breakdown-dimension"
      >
        <breakdown-selector
          :id="dim.id"
          :name="dim.name[lng]"
          :selected="breakdownSelection[dim.id]"
          :options="dim.options"
          :constraints="constraints.find(d => d.id === dim.id)"
          :is-loading="isLoading"
          :disabled="isPrintable"
          @update="updateSelection('breakdowns', $event)"
        />
      </div>
      <div class="indicator-header__breakdown-dimension lollipop">
        <lollipop-selector
          v-if="!isNDSC && chartType === 'barchart' && lollipopDimension"
          :selection="lollipopOpts"
          :current-dimension="lollipopDimension"
          :dimensions="breakdownDimensions"
          :constraints="constraints"
          :disabled="isPrintable"
          :is-loading="isLoading"
          @update="updateLollipop($event)"
          @switch-dimension="setLollipopDimension($event)"
        />
      </div>
    </div>
    <div class="indicator-header__section country">
      <div v-if="!isPrintable" class="indicator-header__country-dimension">
        <country-selector
          :selection="countrySelection"
          :countries="dataCountries"
          :is-loading="isLoading"
          @update="updateSelection('countries', $event)"
        />
        <div class="indicator-header__country-filter">
          <switcher-selector v-model="countryFilter" :disabled="countrySelection.length == 0" />
          <label :fade-out="countrySelection.length == 0">{{ hideUnselectedLabel }}</label>
        </div>
        <div class="indicator-header__country-filter show-full-label">
          <switcher-selector v-model="fullLabels" />
          <label>{{ $lng('show-full-labels') }}</label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import isFlipped from '@/mixins/isFlipped'
import CountrySelector from '../selectors/CountrySelector.vue'
import BreakdownSelector from '../selectors/BreakdownSelector.vue'
import SwitcherSelector from '../selectors/SwitcherSelector.vue'
import LollipopSelector from '../selectors/LollipopSelector.vue'
import FlipSelector from '../selectors/FlipSelector.vue'

import { flatten as flattenTree } from '@/utils/tree.js'
import { filterChartRowByTimeScale } from '@/utils/dates'

export default {
  components: {
    CountrySelector,
    BreakdownSelector,
    SwitcherSelector,
    LollipopSelector,
    FlipSelector,
  },
  mixins: [isFlipped],
  props: [
    'dataCountries',
    'countrySelection',
    'constraints',
    'breakdownSelection',
    'breakdownDimensions',
    'flipDimensions',
  ],
  computed: {
    indId() {
      return this.$store.state.query.mainIndId
    },
    indName() {
      return this.$store.getters.indicatorName('main', this.indId, this.lng)
    },
    countryFilter: {
      get() {
        return this.$store.state.query.countryFilter
      },
      set(to) {
        this.$store.commit('countryFilter', to)
      },
    },
    tagIndicator() {
      return Object.values(this.$store.state.tags.indicatorsMap).find(
        ind => ind.metaId === this.indId
      )
    },
    iconURL() {
      return this.tagIndicator
        ? `url(${process.env.VUE_APP_CMS_IMAGE_URL}/${this.tagIndicator.tagIcon})`
        : ''
    },
    chartType() {
      return this.$store.state.query.chart
    },
    lollipopId() {
      return this.$store.state.query.lollipop || this.breakdownDimensions[0].id
    },
    lollipopDimension() {
      return this.breakdownDimensions.find(dim => dim.id === this.lollipopId)
    },
    lollipopOpts() {
      return this.lollipopDimension
        ? flattenTree(this.lollipopDimension.options).filter(opt =>
            this.$store.state.query.lollipopOpts.includes(opt.id)
          )
        : []
    },
    dropdownColumns() {
      return this.breakdownDimensions.length + ((this.chartType === 'barchart' && 1) || 0)
    },
    hideUnselectedLabel() {
      const dimension = this.$store.getters.mainCountryDimension
      const dimName = this.$lng(dimension.name[this.lng]).toLowerCase()
      return `Show selected ${dimName} only`
    },
    isNDSC() {
      return /DF_NDSC$/i.test(this.$store.state.query.mainCubeId)
    },
    fullLabels: {
      get() {
        return this.$store.state.query.fullLabel
      },
      set(to) {
        this.$store.commit('fullLabel', to)
      },
    },
    isLoading() {
      return this.$store.getters.mainConstraints === void 0 && false
    },
  },
  methods: {
    resetChart() {
      const timeScale =
        this.$store.getters.mainTimeScaleDimension?.options.find(opt => opt.isDefault)?.id ||
        this.$store.getters.mainTimeScales[0]?.id ||
        'A'

      const dataDates = this.$store.getters.mainAvailableDates.filter(date =>
        filterChartRowByTimeScale(date, timeScale)
      )

      const unit =
        this.$store.getters.mainUnitDimension?.options.find(opt => opt.isDefault) ||
        this.$store.getters.mainUnits[0]

      this.$store.dispatch('resetChartQuery', {
        ...this.$store.getters.breakdownDefaults,
        mainUnit: unit?.id,
        time: [dataDates[0].getTime(), dataDates[dataDates.length - 1].getTime()],
        timeScale: timeScale,
      })
    },
    updateSelection(dim, value) {
      if (dim === 'breakdowns') {
        this.$emit('update', {
          slot: 'main',
          dim: dim,
          value: { ...this.breakdownSelection, ...value },
        })
      } else {
        this.$emit('update', { dim, value })
      }
    },
    switchMode() {
      const version = this.$route.params.version
      const versionName = this.$route.params.versionName
      const layout = this.$route.params.layout
      const query = location.hash.replace(/.+(?=\?)\??/, '')
      this.$router.push({
        path: `/explorer/${version}/${versionName}/compare/${layout}/${this.lng}?${query}`,
      })
    },
    setLollipopDimension(dimension) {
      if (dimension.id !== this.lollipopId) {
        this.$store.commit('lollipop', dimension.id)
      }
    },
    updateLollipop(selection) {
      this.$store.commit(
        'lollipopOpts',
        selection.map(d => d.id)
      )
    },
  },
}
</script>

<style lang="stylus">
.indicator-header
  border-bottom: 1px solid $gray
  margin-bottom: vw(5px)
  display: grid
  grid-template-columns: 20% 1fr
  grid-template-rows: 20px repeat(3, auto)

  .mobile &
    border-bottom: none
    padding-top: vw(40px)
    grid-template-rows: 10px repeat(4, auto)

    +m-breakpoint(landscape)
      padding-top: vw(25px)

    &[layout="embedded"]
      padding-top: vw(20px)

      +m-breakpoint(landscape)
        padding-top: vh(10px)

    .indicator-header__section
      &.meta
        grid-row: 1

      &.main
        grid-row: 2

      &.flips
        grid-column: 1 / 3
        grid-row: 3
        border-right: 0px solid

      &.breakdowns
        grid-row: 4
        grid-column: 1 / 3
        align-items: flex-start
        flex-direction: column

      &.country
        grid-column: 1 / 3
        grid-row: 5

  &[layout="print"],
  &[layout="embedded"]
    .indicator-header__title
      width: 90%

      .mobile &
        width: 80%

    .indicator-header__section
      &.main
        margin-bottom: 15px

      &.breakdowns
        align-items: flex-start
        flex-direction: column

        .indicator-header__breakdown-dimension
          //display: block
          margin-right: unset
          max-width: unset

          //width: 100%

  &[layout="embedded"]
    grid-template-columns: 1fr

  &[layout="print"]
    border-bottom: none

.indicator-header__section
  width: 100%

  &.columns
    display: flex
    width: 100%

  &.meta
    height: 20px
    position: relative
    grid-row: 1
    grid-column: 1 / 3

    .mobile &
      height: 20px

      +m-breakpoint(landscape)
        height: 10px

  &.main
    align-items: center
    display: flex
    grid-row: 2
    grid-column: 1 / 3
    padding-bottom: .75em

  &.flips
    grid-row: 3 / 5
    grid-column: 1
    border-right: 1px solid $gray
    display: flex
    justify-content: center
    align-items: center

    [layout="embedded"] &
    [layout="print"] &
      display: none

  &.breakdowns
    grid-row: 3
    grid-column: 2 / 3
    padding-left: 0.5rem

    [layout="embedded"] &
    [layout="print"] &
      padding-left: 0
      grid-column: 1 / 3

      .dropdown-select
        margin-right: 0

    [layout="print"] &
      .indicator-header__breakdown-dimension.lollipop
        display: none

    .mobile &
      padding-left: 0

    &[cols="1"] .indicator-header__breakdown-dimension
    &[cols="2"] .indicator-header__breakdown-dimension
      width: 50%

    &[cols="3"] .indicator-header__breakdown-dimension
      width: 33.3%

    &[cols="4"] .indicator-header__breakdown-dimension
      width: 25%

    &[cols="5"] .indicator-header__breakdown-dimension
      width: 20%

    &[cols]
      .mobile &
        .indicator-header__breakdown-dimension
          max-width: unset

  &.country
    grid-row: 4
    grid-column: 2 / 3
    padding: 0.25rem
    padding-left: 0.5rem

    [layout="embedded"] &
      grid-column: 1 / 3
      padding-left: 0

    [layout="print"] &
      display: none

    .mobile &
      padding: 0

.indicator-header__meta
  flex: 1
  font-weight: 400

.indicator-header__mode-switcher
  position: absolute
  right: vw(-55px)
  top: vh(20px)
  display flex

  .mobile &
    right: vw(-8px)
    top: vw(-30px)
    z-index: 1

    +m-breakpoint(landscape)
      top: vw(-20px)
      right: 0

.indicator-header__reset-chart-btn
.indicator-header__mode-switcher-btn
  color: $green
  cursor: pointer
  display: block
  font-family: "Bernina Sans"
  font-size: .75em
  line-height: 1.2
  padding-top: vw(10px + 1em)

  .mobile &
    font-size: 0.65em

.indicator-header__mode-switcher-btn
  border: 1px solid $gray
  border-radius: 2em
  padding: vw(10px) vw(30px)
  margin-left: vw(10px)

  .mobile &
    padding: vw(7px) vw(24px)

    +m-breakpoint(landscape)
      padding: vw(5px) vw(16px)

  &:hover
    background-color: rgba($green, 0.2)
    color: $green

  &:active
    background-color: $green
    color: #fff

.indicator-header__reset-chart-btn
  margin-right: vw(5px)
  color: $black

  .mobile &
    padding-top: vw(8px)
    margin-right: vw(2px)

    +m-breakpoint(md)
      padding-top: vw(5px)

  &:hover
    text-decoration: underline
    color: #000

.indicator-header__icon
  background-repeat: no-repeat
  background-size: contain
  height: vw(40px)
  margin-right: vw(10px)
  width: vw(40px)

  .mobile &
    +m-breakpoint(landscape)
      height: vw(20px)
      width: vw(20px)
      margin-right: vw(5px)

.indicator-header__title
  color: $blue
  width: 75%
  m-font-size(24, 30)

  .mobile &
    m-font-size(18, 26)
    width: 90%

    +m-breakpoint(landscape)
      width: 85%
      font-size: 1em
      line-height: 1.3

.indicator-header__breakdown-dimension
  display: inline-block
  position: relative
  padding-right: unset
  max-width: vw(300px)

  &.lollipop
    min-width: vw(320px)

  .mobile &
    padding-right: unset
    max-width: unset
    display: block
    width: 100% !important

    > div
      border-bottom: 1px solid rgba($dark-gray, 0)

      &.open
        border-bottom-color: transparent

.indicator-header__country-dimension
  align-items: center
  display: flex !important

  .country-selector
    flex-grow: 0
    min-width: $dropdown-width

    .mobile &
      border-bottom: unset
      flex-grow: 1
      min-width: unset

  .mobile &
    display: flex
    flex-wrap: wrap

.indicator-header__country-filter
  display: flex
  align-items: center
  padding-left: 1em

  &.show-full-label
    margin-left: 1em

  .mobile &
    border: none !important
    margin-left: vw(10px)
    padding-left: 0

    &.show-full-label
      display: none

  .switcher
    .mobile &
      margin-bottom: auto

  label
    text-indent: 1.5em
    font-size: 0.7em

    .mobile &
      text-indent: 0
      font-size: 0.55em
      margin-bottom: vw(2px)
      width: 75px
      line-height: 1

      +m-breakpoint(landscape)
        width: 90px

    &[fade-out]
      opacity: 0.5
</style>
